import httpClient from '../../../shared/lib/httpClient';
import { ENDPOINTS } from '../../../shared/services/api/endpoints';
import { useNotificationStore } from '../store/notificationStore';

/**
 * Get user notification settings
 * @returns {Promise<{ success: boolean, data: object, message?: string }>}
 */
export async function getNotificationSettings() {
  try {
    const response = await httpClient.get(ENDPOINTS.IDENTITY.NOTIFICATION_SETTINGS);
    return {
      success: true,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to fetch notification settings.';
    console.error('Error fetching notification settings:', error);
    return { success: false, message };
  }
}

/**
 * Update user notification settings
 * @param {object} settings - Notification settings to update
 * @param {boolean} settings.emailNotifications - Email notifications preference
 * @param {boolean} settings.productUpdates - Product updates preference
 * @param {boolean} settings.newsletter - Newsletter preference
 * @param {boolean} settings.billingEmails - Billing emails preference
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
/**
 * Syncs a single newsletter (DataNet) value to the DB notification settings.
 * Fetches current settings from the store (loading from API if needed), then
 * saves the full settings object with the updated newsletter value.
 * Call this whenever DataNet is toggled outside the Notifications tab.
 */
export async function syncNewsletter(newsletterValue) {
  const store = useNotificationStore.getState();

  let settings = store.notificationSettings;
  if (!store.hasLoaded) {
    const result = await getNotificationSettings();
    if (result.success) {
      store.setNotificationSettings(result.data);
      store.setLoading && useNotificationStore.setState({ hasLoaded: true });
      settings = result.data;
    }
  }

  const updated = { ...settings, newsletter: newsletterValue };
  useNotificationStore.getState().setNotificationSetting('newsletter', newsletterValue);
  return updateNotificationSettings(updated);
}

/**
 * The API describes categories to us as a list (key, label, description, order, isEnabled) so the
 * UI can render them without knowing what they are, but it accepts them back as a plain
 * key -> enabled map. Labels and ordering are server-owned and read-only, so there is nothing to
 * send back but the flag.
 */
function toCategoryMap(categories) {
  if (!Array.isArray(categories)) return undefined;
  return categories.reduce((map, category) => {
    map[category.key] = category.isEnabled;
    return map;
  }, {});
}

export async function updateNotificationSettings(settings) {
  try {
    const payload = { ...settings, categories: toCategoryMap(settings.categories) };
    const response = await httpClient.put(ENDPOINTS.IDENTITY.NOTIFICATION_SETTINGS, payload);
    return {
      success: true,
      data: response.data.data,
      message: response.data.message || 'Notification settings updated successfully'
    };
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to update notification settings.';
    console.error('Error updating notification settings:', error);
    return { success: false, message };
  }
}
